import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { isApiProblem } from '../lib/api';
import { useTenant } from '../lib/tenant-context';
import { Panel } from './Panel';
import { SupportLink } from './SupportLink';
import { Button } from './Button';

type ProblemNoticeProps = { error: unknown; className?: string; compact?: boolean; onRetry?: () => void };

export function TechnicalDetails({ children }: { children: ReactNode }) {
  return (
    <details className="mt-2 text-[13px] text-inkMuted">
      <summary className="disclosure text-[13px] font-normal">Technical details</summary>
      <div className="mono break-words pb-2 [overflow-wrap:anywhere]">{children}</div>
    </details>
  );
}

/**
 * Keep the recovery in view and diagnostic references available on request. Never replay a
 * mutation. The error colour is carried by the title line, a real error; the surface is a standard
 * card (or, compact, no surface at all), never a coloured stripe (S-19).
 */
export function ProblemNotice({ error, className = '', compact = false, onRetry }: ProblemNoticeProps) {
  const tenant = useTenant();
  if (!error) return null;
  const problem = isApiProblem(error) ? error.problem : null;
  const offline = typeof navigator !== 'undefined' && !navigator.onLine;
  const title = problem
    ? problem.title
    : offline
      ? 'You are offline.'
      : error instanceof Error && error.name === 'ZodError'
        ? 'This page could not read the server’s response.'
        : 'The request could not be completed.';
  const detail =
    problem?.detail ??
    (offline
      ? 'Reconnect to the internet. If you were saving, check the result before repeating the action.'
      : null);
  const inner = (
    <>
      <p className="text-[14px] font-medium text-dangerText">{title}</p>
      {detail && <p className="mt-1 text-[13px] text-inkMuted">{detail}</p>}
      {problem && (
        <TechnicalDetails>
          <p>Request reference: {problem.trace_id}</p>
          <p>Response status: {problem.status}</p>
        </TechnicalDetails>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-x-4">
        {onRetry && (
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        )}
        <SupportLink tenant={tenant} topic="Request failed" />
        <Link
          to="/"
          className="inline-flex min-h-11 items-center text-[14px] text-accentText underline underline-offset-4"
        >
          Return to academy
        </Link>
      </div>
    </>
  );
  if (compact)
    return (
      <div className={className} role="alert">
        {inner}
      </div>
    );
  return (
    <Panel role="alert" className={className}>
      {inner}
    </Panel>
  );
}
